/**
 * Shared timezone-offset validation for wearable observations.
 *
 * Both the FHIR persistence layer (VitalObservation) and the local-day
 * bucketing layer (apps/web/lib/wearables/local-day) need to normalize raw
 * offset values into a sane finite integer. Keeping this in one place
 * prevents the two layers from drifting apart.
 */

// 18h covers every real IANA offset (Kiribati/Line Islands is +14h; some
// historical/maritime zones go to +/-12:45) with margin for clock skew.
export const MAX_TIMEZONE_OFFSET_SECONDS = 18 * 60 * 60;

/**
 * Normalize a raw timezone-offset value (seconds) into a finite integer
 * within ±MAX_TIMEZONE_OFFSET_SECONDS. Returns `undefined` when the input
 * is missing, non-finite, or out of range.
 */
export function normalizeTimezoneOffsetSeconds(
  timezoneOffsetSeconds?: number | null,
): number | undefined {
  if (timezoneOffsetSeconds === undefined || timezoneOffsetSeconds === null) {
    return undefined;
  }

  if (!Number.isFinite(timezoneOffsetSeconds)) {
    return undefined;
  }

  const truncatedOffsetSeconds = Math.trunc(timezoneOffsetSeconds);
  if (Math.abs(truncatedOffsetSeconds) > MAX_TIMEZONE_OFFSET_SECONDS) {
    return undefined;
  }

  return truncatedOffsetSeconds;
}
